import { create } from "zustand";
import { getFoscams, getIfLoggedInAsync, getLogout } from "@client/api/endpoints";
import { useMetricMetaStore } from "./metricMeta";
import { useFoscamStore } from "./foscam";

export type UserState = UserSafe | null | false; // null = initializing, false = not logged in

interface UserStore {
  user: UserState;
  foscams: FoscamReact[];
  initialLoadDone: boolean;
  setUser: (u: UserState) => void;
  setFoscams: (f: FoscamReact[]) => void;
  fetchInitial: () => Promise<void>;
  fetchFoscams: () => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useUserStore = create<UserStore>((set, get) => ({
  user: null,
  foscams: [],
  initialLoadDone: false,

  setUser: (u) => set({ user: u }),
  setFoscams: (f) => set({ foscams: f }),

  fetchInitial: async () => {
    if (get().initialLoadDone) return;
    try {
      const meRes = await getIfLoggedInAsync();
      if (meRes.responseObject) {
        set({ user: meRes.responseObject });
        // Paralelně načti foscams + schopnosti kamer + topic metadata — vše nezávislé.
        await Promise.all([
          get().fetchFoscams(),
          useFoscamStore.getState().fetchCaps(),
          useMetricMetaStore.getState().load(),
        ]);
      } else {
        set({ user: false });
      }
    } catch {
      set({ user: false });
    } finally {
      set({ initialLoadDone: true });
    }
  },

  fetchFoscams: async () => {
    try {
      const foscamsRes = await getFoscams();
      set({ foscams: foscamsRes.responseObject ?? [] });
    } catch {
      set({ foscams: [] });
    }
  },

  refreshUser: async () => {
    try {
      const res = await getIfLoggedInAsync();
      set({ user: res.responseObject || false });
    } catch {
      set({ user: false });
    }
  },

  logout: async () => {
    try {
      await getLogout();
    } catch {
      // ignore - logout endpoint redirectuje
    }
    set({ user: false, foscams: [] });
  },
}));

// Listener na 401 z axios interceptoru — vynuluj user state.
if (typeof window !== "undefined") {
  window.addEventListener("auth:unauthorized", () => {
    useUserStore.getState().setUser(false);
  });
}
